import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {PageUser} from '~shared/models/wiki';

import {createPageWithDetails} from '~ui/store/thunks/pages';

import {getPageAcl, updatePageSettings} from '../pages/thunks';
import {getResolvedUserInfo} from '../user/thunks';
import {requestAuthorRole} from '../../thunks';

import {resolveUsersIdentitiesThunk} from './thunks';
import {setUserDetails} from './utils';

export type UserState = PageUser;

export interface UsersState {
    current: UserState['id'] | null;
    entities: Record<UserState['id'], UserState>;
    byIdentity: Record<string, UserState['id']>;
    ids: UserState['id'][];
}

const initialState: UsersState = {
    current: null,
    entities: {},
    byIdentity: {},
    ids: [],
};

export type SetManyPayload = UserState[];

const {reducer, actions} = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setMany(state, {payload}: PayloadAction<SetManyPayload>) {
            payload.forEach((user) => {
                setUserDetails(state, user);
            });
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(resolveUsersIdentitiesThunk.fulfilled, (state, action) => {
                const {users} = action.payload;
                users.forEach((user) => {
                    setUserDetails(state, user);
                });
            })
            .addCase(getResolvedUserInfo.fulfilled, (state, action) => {
                const user = action.payload.users[0];
                if (!user) {
                    return;
                }

                setUserDetails(state, user);
            })
            .addCase(createPageWithDetails.fulfilled, (state, action) => {
                if (!action.payload) {
                    return;
                }

                const authors = action.payload.authors;

                authors.all.forEach((user) => {
                    setUserDetails(state, user);
                });

                setUserDetails(state, authors.owner);
            })
            .addCase(requestAuthorRole.fulfilled, (state, action) => {
                if (!action.payload) {
                    return;
                }

                const authors = action.payload.authors;

                authors.all.forEach((user) => {
                    setUserDetails(state, user);
                });

                setUserDetails(state, authors.owner);
            })
            .addCase(updatePageSettings.fulfilled, (state, action) => {
                if (!action.payload) {
                    return;
                }

                const authors = action.payload.authors;

                if (authors) {
                    authors.all.forEach((user) => {
                        setUserDetails(state, user);
                    });

                    setUserDetails(state, authors.owner);
                }

                action.payload.acl?.users?.forEach((user) => {
                    setUserDetails(state, user);
                });
            })
            .addCase(getPageAcl.fulfilled, (state, action) => {
                if (!action.payload) {
                    return;
                }

                action.payload.acl?.users?.forEach((user) => {
                    setUserDetails(state, user);
                });
            }),
});

export {actions, reducer};
